import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Checkbox } from "antd";
import { useTranslation } from "react-i18next";

import DateEditor from "@tailormed/common-client/component/inline-edit/editors/DateEditor";

// Mirrors ApprovedFields.js: pick a date OR check "Valid Indefinitely" (mutually exclusive).
const EnrollmentValidThroughInlineEditor = ({
  value,
  effectiveIndefinitely: initialEffectiveIndefinitely = false,
  onCommit,
  onCancel,
  autoFocus = true,
  placeholder
}) => {
  const { t } = useTranslation();
  const [isIndefinite, setIsIndefinite] = useState(Boolean(initialEffectiveIndefinitely));
  const isIndefiniteRef = useRef(isIndefinite);

  useEffect(() => {
    isIndefiniteRef.current = isIndefinite;
  }, [isIndefinite]);

  const handleDateCommit = (newDate) => {
    if (typeof onCommit !== "function") return;
    // Mandatory for approved enrollments: ignore a bare clear (must pick a date or check "Valid Indefinitely").
    if (!newDate && !isIndefiniteRef.current) {
      if (typeof onCancel === "function") onCancel();
      return;
    }
    // Picking a date wins over the indefinite checkbox.
    setIsIndefinite(false);
    onCommit({ validThrough: newDate, effectiveIndefinitely: false });
  };

  const handleCheckboxChange = (event) => {
    setIsIndefinite(event.target.checked);
  };

  // On blur, persist an indefinite check as a commit (Esc still routes through DateEditor → cancel).
  const handleCancel = () => {
    if (isIndefiniteRef.current) {
      onCommit({ validThrough: null, effectiveIndefinitely: true });
      return;
    }
    if (typeof onCancel === "function") onCancel();
  };

  const checkbox = (
    <Checkbox checked={isIndefinite} onChange={handleCheckboxChange} onMouseDown={(e) => e.preventDefault()}>
      <span style={{ fontWeight: 600 }}>{t("patientApplications.valid_indefinitely")}</span>
    </Checkbox>
  );

  return (
    <DateEditor
      autoFocus={autoFocus}
      value={isIndefinite ? null : value}
      placeholder={placeholder}
      onCommit={handleDateCommit}
      onCancel={handleCancel}
      secondaryControl={checkbox}
    />
  );
};

EnrollmentValidThroughInlineEditor.propTypes = {
  value: PropTypes.any,
  effectiveIndefinitely: PropTypes.bool,
  onCommit: PropTypes.func,
  onCancel: PropTypes.func,
  autoFocus: PropTypes.bool,
  placeholder: PropTypes.node
};

export default EnrollmentValidThroughInlineEditor;
